import { ref, computed, watch, onMounted } from 'vue'
import type { ThemeMode, ResolvedTheme } from '../types/theme'
import { applyThemeConfig } from '../themes'
import { logger } from '../utils/logger'

const STORAGE_KEY = 'portfolio-theme'
const THEME_ATTRIBUTE = 'data-theme'

/**
 * Theme-Management Composable
 * 
 * Features:
 * - System-Präferenz Detection
 * - LocalStorage Persistenz
 * - Smooth Theme-Wechsel
 * - Erweiterbar für zukünftige Themes
 */
/**
 * Ermittelt die System-Präferenz des Benutzers
 */
function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

/**
 * Liest das gespeicherte Theme aus dem LocalStorage
 */
function getStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light'
  const STORAGE_KEY = 'portfolio-theme'
  const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null
  return stored && (stored === 'light' || stored === 'dark') ? stored : 'light'
}

// Singleton-Instanz für Theme-State mit reaktiven Refs
let themeState: {
  themeMode: ThemeMode
  systemPreference: ResolvedTheme
  initialized: boolean
} | null = null

// Singleton refs - diese werden über alle useTheme() Aufrufe geteilt
let themeModeRef: ReturnType<typeof ref<ThemeMode>> | null = null
let systemPreferenceRef: ReturnType<typeof ref<ResolvedTheme>> | null = null

/**
 * Initialisiert den Theme-State beim ersten Aufruf
 */
function initThemeState() {
  if (themeState) return themeState

  const storedMode = getStoredThemeMode()
  const sysPref = getSystemPreference()

  themeState = {
    themeMode: storedMode,
    systemPreference: sysPref,
    initialized: false
  }

  return themeState
}

export function useTheme() {
  // Initialisiere den State beim ersten Aufruf
  const state = initThemeState()

  // Verwende Singleton refs - erstelle sie nur beim ersten Aufruf
  if (!themeModeRef) {
    themeModeRef = ref<ThemeMode>(state.themeMode)
  }
  if (!systemPreferenceRef) {
    systemPreferenceRef = ref<ResolvedTheme>(state.systemPreference)
  }

  // TypeScript weiß, dass diese Refs jetzt initialisiert sind
  const themeMode = themeModeRef!
  const systemPreference = systemPreferenceRef!

  /**
   * Ermittelt das aufgelöste Theme (light oder dark)
   */
  const resolvedTheme = computed<ResolvedTheme>(() => {
    if (themeMode.value === 'system') {
      return systemPreference.value || 'light'
    }
    return (themeMode.value === 'light' || themeMode.value === 'dark') ? themeMode.value : 'light'
  })

  /**
   * Setzt das Theme auf dem DOM
   */
  const applyTheme = (theme: ResolvedTheme) => {
    if (typeof document === 'undefined') return

    const html = document.documentElement

    // Wichtig: Entferne dark-Klasse IMMER zuerst, dann füge sie hinzu wenn nötig
    // Dies stellt sicher, dass Tailwind die Änderung erkennt
    // Verwende toggle mit force, um sicherzustellen, dass die Klasse korrekt gesetzt wird
    const shouldBeDark = theme === 'dark'
    const isDark = html.classList.contains('dark')

    if (shouldBeDark && !isDark) {
      html.classList.add('dark')
    } else if (!shouldBeDark && isDark) {
      html.classList.remove('dark')
    }

    html.setAttribute(THEME_ATTRIBUTE, theme)

    // Debug: Log für Entwicklung
    logger.debug(`Theme applied: ${theme}, dark class present: ${html.classList.contains('dark')}`)

    // Nutze Theme Registry für zukünftige Erweiterungen (z.B. CSS-Variablen)
    applyThemeConfig(theme)

    // Force reflow to ensure styles are recalculated
    // Trigger multiple reflows to ensure Tailwind picks up the change
    void html.offsetHeight
    requestAnimationFrame(() => {
      void html.offsetHeight
      // Zusätzlicher Force-Update nach einem Frame
      requestAnimationFrame(() => {
        void html.offsetHeight
      })
    })
  }

  /**
   * Setzt ein neues Theme
   */
  const setTheme = (mode: ThemeMode) => {
    themeMode.value = mode
    if (state) {
      state.themeMode = mode
    }

    // Speichere in LocalStorage (nur wenn nicht 'system')
    if (mode === 'system') {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(STORAGE_KEY, mode)
    }

    // Wende Theme sofort an
    applyTheme(resolvedTheme.value)
  }

  /**
   * Togglet zwischen light und dark (überspringt 'system')
   */
  const toggleTheme = () => {
    const currentResolved = resolvedTheme.value
    const newTheme: ResolvedTheme = currentResolved === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }

  /**
   * Initialisiert das Theme-System
   * Muss früh aufgerufen werden, um FOUT zu vermeiden
   */
  const initializeTheme = () => {
    if (!state) return

    // Verhindere mehrfache Initialisierung
    if (state.initialized && typeof window !== 'undefined') {
      // Synchronisiere mit aktuellem DOM-Zustand
      const html = document.documentElement
      const isDark = html.classList.contains('dark')
      const currentResolved = isDark ? 'dark' : 'light'

      // Aktualisiere nur wenn nötig
      if (resolvedTheme.value !== currentResolved) {
        applyTheme(resolvedTheme.value)
      }
      return
    }

    // Der State wurde bereits in initThemeState() initialisiert
    // Wende Theme sofort an, um sicherzustellen, dass das DOM synchron ist
    applyTheme(resolvedTheme.value)

    // Höre auf Änderungen der System-Präferenz (nur einmal)
    if (typeof window !== 'undefined' && !state.initialized) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      const handleSystemChange = (e: MediaQueryListEvent) => {
        const newPref = e.matches ? 'dark' : 'light'
        systemPreference.value = newPref
        if (state) {
          state.systemPreference = newPref
        }

        // Nur aktualisieren wenn 'system' aktiv ist
        if (themeMode.value === 'system') {
          applyTheme(resolvedTheme.value)
        }
      }

      // Moderne Browser
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleSystemChange)
      } else {
        // Fallback für ältere Browser
        mediaQuery.addListener(handleSystemChange)
      }

      state.initialized = true
    }
  }

  // Watch für Theme-Änderungen
  watch(resolvedTheme, (newTheme) => {
    applyTheme(newTheme)
  }, { immediate: false })

  // Initialisiere sofort (nicht erst beim Mount)
  if (typeof window !== 'undefined') {
    initializeTheme()
  } else {
    // Fallback für SSR
    onMounted(() => {
      initializeTheme()
    })
  }

  const isDark = computed(() => resolvedTheme.value === 'dark')

  return {
    themeMode: computed(() => themeMode.value),
    resolvedTheme,
    isDark,
    systemPreference: computed(() => systemPreference.value),
    setTheme,
    toggleTheme,
    initializeTheme
  }
}

